import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'

function LoanPayment() {
  const navigate = useNavigate()
  const [loans, setLoans] = useState([])
  const [selectedLoan, setSelectedLoan] = useState(null)
  const [formData, setFormData] = useState({
    noPinjaman: '',
    jumlahBayar: 0,
    sumberDana: 'kas',
    keterangan: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const loanSelectRef = useRef(null)
  const [showPrintModal, setShowPrintModal] = useState(false)
  const [printData, setPrintData] = useState(null)
  const [namaKoperasi, setNamaKoperasi] = useState('Koperasi')
  const [calculation, setCalculation] = useState(null)
  const [allocation, setAllocation] = useState(null)
  const [systemConfig, setSystemConfig] = useState({})
  const [simpananWajibStatus, setSimpananWajibStatus] = useState({ checked: false, paid: false, message: '' })

  useEffect(() => {
    fetchActiveLoans()
    fetchSystemConfig()
  }, [])

  const fetchSystemConfig = async () => {
    try {
      const response = await api.get('/settings/config')
      // Handle nested response: response.data.data.data
      const configData = response.data?.data?.data || response.data?.data || response.data || {}
      setSystemConfig(configData)
    } catch (error) {
      console.error('Error fetching system config:', error)
    }
  }

  useEffect(() => {
    if (loanSelectRef.current && loans.length > 0) {
      // Initialize Select2
      $(loanSelectRef.current).select2({
        placeholder: '-- Pilih Pinjaman --',
        allowClear: true,
        width: '100%'
      })

      // Handle change event
      $(loanSelectRef.current).on('change', (e) => {
        const noPinjaman = $(e.target).val()
        handleLoanChange(noPinjaman)
      })

      // Cleanup
      return () => {
        if (loanSelectRef.current) {
          $(loanSelectRef.current).off('change').select2('destroy')
        }
      }
    }
  }, [loans])

  const fetchActiveLoans = async () => {
    try {
      const response = await api.get('/loans?status=Aktif')
      setLoans(response.data.data || [])
    } catch (error) {
      console.error('Error fetching loans:', error)
    }
  }

  // Cek simpanan wajib anggota di bulan ini
  const checkSimpananWajib = async (noAnggota) => {
    try {
      const now = new Date()
      const bulan = now.getMonth() + 1
      const tahun = now.getFullYear()
      
      const response = await api.get(`/savings/simpanan-wajib/check?noAnggota=${noAnggota}&bulan=${bulan}&tahun=${tahun}`)
      
      if (response.data?.success) {
        const data = response.data.data
        setSimpananWajibStatus({
          checked: true,
          paid: data.sudahBayar,
          message: data.sudahBayar ? 'Simpanan wajib bulan ini sudah dibayar' : `Simpanan wajib bulan ini belum dibayar (Rp ${data.jumlahWajib?.toLocaleString('id-ID') || 0})`
        })
        return data.sudahBayar
      }
    } catch (error) {
      setSimpananWajibStatus({
        checked: true,
        paid: false,
        message: 'Gagal memeriksa status simpanan wajib'
      })
      return false
    }
  }

  const handleLoanChange = async (noPinjaman) => {
    const selected = loans.find(l => l.NoPinjaman === noPinjaman)
    setSelectedLoan(selected)
    
    // Reset simpanan wajib status
    setSimpananWajibStatus({ checked: false, paid: false, message: '' })
    
    // Cek simpanan wajib jika ada anggota yang dipilih
    const noAnggota = selected?.NBA || selected?.NoAnggota
    if (noAnggota) {
      await checkSimpananWajib(noAnggota)
    }

    if (selected && selected.TipeBunga === 'Efektif') {
      setFormData({
        ...formData,
        noPinjaman
      })
      // calculatePaymentPreview will be called by useEffect when systemConfig is ready
    } else {
      setSelectedLoan(null)
      setCalculation(null)
    }
  }

  // Trigger calculatePaymentPreview when selectedLoan or systemConfig changes
  useEffect(() => {
    if (selectedLoan && systemConfig && Object.keys(systemConfig).length > 0) {
      calculatePaymentPreview(selectedLoan)
    }
  }, [selectedLoan, systemConfig])

  const calculatePaymentPreview = async (loan) => {
    try {
      // Get last payment
      const response = await api.get(`/loans/${loan.NoPinjaman}`)
      const loanData = response.data.data

      const currentDate = new Date()
      const currentYear = currentDate.getFullYear()
      const currentMonth = currentDate.getMonth()

      // Get last bunga payment (Bunga > 0 indicates actual interest payment)
      let lastPaymentYear, lastPaymentMonth, hasBungaPayment = false
      let lastPaymentDate
      try {
        const paymentResponse = await api.get(`/loans/${loan.NoPinjaman}/payments?limit=1`)
        const lastPayment = paymentResponse.data.data?.[0]
        // Only count as bunga payment if Bunga > 0 (not pencairan)
        if (lastPayment && parseFloat(lastPayment.Bunga) > 0) {
          const paymentDate = new Date(lastPayment.TglBayar)
          lastPaymentYear = paymentDate.getFullYear()
          lastPaymentMonth = paymentDate.getMonth()
          hasBungaPayment = true
          lastPaymentDate = paymentDate
        }
      } catch (e) {
        // No bunga payments yet
      }

      // If no bunga payments yet, use TglPencairan for calculation reference
      if (!hasBungaPayment) {
        const pencairanDate = new Date(loanData.TglPencairan || currentDate)
        lastPaymentYear = pencairanDate.getFullYear()
        lastPaymentMonth = pencairanDate.getMonth()
        lastPaymentDate = pencairanDate
      }

      // Calculate days since last payment/pencairan (for prorata calculation)
      const daysSinceLastPayment = Math.floor((currentDate - lastPaymentDate) / (1000 * 60 * 60 * 24))

      // Calculate months difference (calendar months)
      // Example: Maret (2) to April (3) = 2 months (Maret + April)
      let monthsCount = (currentYear - lastPaymentYear) * 12 + (currentMonth - lastPaymentMonth)
      // Add 1 because we include the current month
      monthsCount = Math.max(monthsCount + 1, 1)

      const sisaPokok = parseFloat(loanData.SisaPokok)
      const bungaPerTahun = parseFloat(loanData.Bunga)

      // Bunga per bulan = pokok * rate / 12
      const bungaPerBulan = sisaPokok * (bungaPerTahun / 100) / 12

      // Check if bunga for current month has already been paid
      // Only TRUE if there was an actual bunga payment in current month
      const bungaSudahDibayar = hasBungaPayment && (lastPaymentYear === currentYear && lastPaymentMonth === currentMonth)

      // Get tunggakan from loan data
      const tunggakanBunga = parseFloat(loanData.TunggakanBunga || 0)
      const tunggakanDenda = parseFloat(loanData.TunggakanDenda || 0)

      // Get prorata setting from system config
      // Try multiple paths for prorata setting
      const prorataAktif = 
        systemConfig?.pinjaman?.bunga_prorata_aktif?.value === '1' ||
        systemConfig?.pinjaman?.bunga_prorata_aktif === '1' ||
        systemConfig?.bunga_prorata_aktif?.value === '1' ||
        systemConfig?.bunga_prorata_aktif === '1' ||
        false

      // Helper function to round to nearest 500
      const roundTo500 = (amount) => {
        return Math.round(amount / 500) * 500
      }

      // Only calculate bunga for months that haven't been paid yet
      let bungaBulanBerjalan = 0
      if (!bungaSudahDibayar) {
        if (prorataAktif) {
          // Bulan pertama (pencairan): prorata dari tanggal pencairan ke akhir bulan pencairan
          const pencairanDate = new Date(loanData.TglPencairan)
          const endOfMonth = new Date(pencairanDate.getFullYear(), pencairanDate.getMonth() + 1, 0)
          const daysProrata = endOfMonth.getDate() - pencairanDate.getDate() + 1
          const firstMonthBunga = roundTo500((bungaPerBulan / 30) * daysProrata)

          // Bulan berikutnya: penuh
          const fullMonths = Math.max(monthsCount - 1, 0)
          const fullMonthsBunga = Math.round(bungaPerBulan * fullMonths)

          bungaBulanBerjalan = firstMonthBunga + fullMonthsBunga
        } else {
          bungaBulanBerjalan = Math.round(bungaPerBulan * monthsCount)
        }
      }

      // Denda: 1% per bulan keterlambatan setelah 1 bulan
      const denda = monthsCount > 1 ? bungaBulanBerjalan * 0.01 * (monthsCount - 1) : 0

      const calcData = {
        sisaPokok,
        bungaBulanBerjalan,
        bungaPerBulan,
        monthsCount: monthsCount || 1,
        denda,
        totalKewajiban: bungaBulanBerjalan + denda,
        tunggakanBunga,
        tunggakanDenda,
        lastPaymentMonth,
        lastPaymentYear,
        currentMonth,
        currentYear,
        bungaSudahDibayar,
        // Debug info for prorata
        prorataAktif,
        daysSinceLastPayment,
        tglPencairan: loanData.TglPencairan,
        isProrataApplied: prorataAktif && daysSinceLastPayment < 30 && !bungaSudahDibayar
      }
      setCalculation(calcData)

      // Calculate allocation if jumlahBayar already entered
      if (formData.jumlahBayar > 0) {
        calculateAllocation(formData.jumlahBayar, calcData)
      }
    } catch (error) {
      console.error('Error calculating preview:', error)
    }
  }

  const calculateAllocation = (jumlahBayar, calc) => {
    if (!calc) return

    // Get tunggakan values from calc
    const tunggakanDenda = parseFloat(calc.tunggakanDenda || 0)
    const tunggakanBunga = parseFloat(calc.tunggakanBunga || 0)
    
    // Total kewajiban
    const totalBungaBerjalan = calc.bungaBulanBerjalan || 0
    const totalDendaBerjalan = calc.denda || 0
    const kewajibanBungaDenda = totalBungaBerjalan + tunggakanBunga + tunggakanDenda + totalDendaBerjalan
    
    let sisaBayar = jumlahBayar

    // 1. Bayar bunga bulan berjalan dulu
    const alokasiBunga = Math.min(sisaBayar, totalBungaBerjalan)
    sisaBayar -= alokasiBunga

    // 2. Bayar tunggakan bunga
    const alokasiTunggakanBunga = Math.min(sisaBayar, tunggakanBunga)
    sisaBayar -= alokasiTunggakanBunga

    // 3. Bayar tunggakan denda
    const alokasiTunggakanDenda = Math.min(sisaBayar, tunggakanDenda)
    sisaBayar -= alokasiTunggakanDenda

    // 4. Bayar denda berjalan
    const alokasiDenda = Math.min(sisaBayar, totalDendaBerjalan)
    sisaBayar -= alokasiDenda

    // 5. Sisanya masuk ke pokok
    const alokasiPokok = sisaBayar
    const sisaPokokBaru = calc.sisaPokok - alokasiPokok

    // Total bunga yang dibayar (untuk display)
    const totalAlokasiBunga = alokasiBunga + alokasiTunggakanBunga
    const totalAlokasiDenda = alokasiTunggakanDenda + alokasiDenda

    setAllocation({
      jumlahBayar,
      alokasiBunga,
      alokasiTunggakanBunga,
      alokasiTunggakanDenda,
      alokasiDenda,
      totalAlokasiBunga,
      totalAlokasiDenda,
      alokasiPokok,
      sisaPokokBaru: Math.max(0, sisaPokokBaru),
      kewajibanBungaDenda,
      isComplete: jumlahBayar >= kewajibanBungaDenda || kewajibanBungaDenda === 0,
      tunggakanBunga,
      tunggakanDenda
    })
  }

  const handleJumlahBayarChange = (value) => {
    const jumlah = parseFloat(value) || 0
    setFormData({ ...formData, jumlahBayar: jumlah })
    if (calculation) {
      calculateAllocation(jumlah, calculation)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    // Validasi simpanan wajib harus sudah dibayar
    if (simpananWajibStatus.checked && !simpananWajibStatus.paid) {
      setError('Transaksi ditolak: Anggota belum melakukan simpanan wajib bulan ini. Silakan lakukan transaksi Setoran Reguler terlebih dahulu.')
      setLoading(false)
      return
    }

    try {
      const response = await api.post(`/loans/${formData.noPinjaman}/pay-flexible`, {
        jumlahBayar: formData.jumlahBayar,
        sumberDana: formData.sumberDana
      })

      setNamaKoperasi(response.data.data.namaKoperasi || 'Koperasi')
      setPrintData({
        noPinjaman: formData.noPinjaman,
        namaAnggota: selectedLoan?.NamaAnggota,
        nba: selectedLoan?.NBA,
        jumlahBayar: formData.jumlahBayar,
        alokasiBunga: response.data.data.alokasiBunga,
        alokasiPokok: response.data.data.alokasiPokok,
        alokasiDenda: response.data.data.alokasiDenda,
        tunggakanBungaBaru: response.data.data.tunggakanBungaBaru,
        tunggakanDendaBaru: response.data.data.tunggakanDendaBaru,
        sisaPokokBaru: response.data.data.sisaPokok,
        tanggal: new Date().toLocaleDateString('id-ID')
      })
      setShowPrintModal(true)
      setFormData({
        ...formData,
        noPinjaman: '',
        jumlahBayar: 0,
        sumberDana: 'kas',
        keterangan: ''
      })
      setSelectedLoan(null)
      setCalculation(null)
      fetchActiveLoans()
    } catch (error) {
      setError(error.response?.data?.message || 'Gagal melakukan pembayaran')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value || 0)
  }

  return (
    <div className="row">
      <div className="col-md-8">
        <div className="card shadow border-0">
          <div className="card-header bg-primary text-white">
            <h5 className="m-0 font-weight-bold">Pembayaran Pinjaman Reguler</h5>
          </div>
          <div className="card-body">
            {error && <div className="alert alert-danger">{error}</div>}

            {simpananWajibStatus.checked && (
              <div className={`alert ${simpananWajibStatus.paid ? 'alert-success' : 'alert-warning'} mb-3`}>
                <i className={`fas ${simpananWajibStatus.paid ? 'fa-check-circle' : 'fa-exclamation-triangle'} mr-2`}></i>
                {simpananWajibStatus.message}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label">Pilih Pinjaman *</label>
                <select
                  ref={loanSelectRef}
                  className="form-control select2"
                  style={{ width: '100%' }}
                  disabled={loading}
                >
                  <option value="">-- Pilih Pinjaman --</option>
                  {loans.map((loan) => (
                    <option key={loan.NoPinjaman} value={loan.NoPinjaman}>
                      {loan.NoPinjaman} - {loan.NamaAnggota} - {formatCurrency(loan.Plafond)}
                    </option>
                  ))}
                </select>
              </div>

              {selectedLoan && selectedLoan.TipeBunga !== 'Efektif' && (
                <div className="alert alert-warning">
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  Pembayaran fleksibel hanya untuk pinjaman dengan bunga efektif. Pinjaman ini menggunakan bunga {selectedLoan.TipeBunga}.
                </div>
              )}

              {selectedLoan && selectedLoan.TipeBunga === 'Efektif' && (
                <>
                  <div className="alert alert-info mb-3">
                    <strong>NBA:</strong> {selectedLoan.NBA} <br />
                    <strong>Nama:</strong> {selectedLoan.NamaAnggota} <br />
                    <strong>Plafond:</strong> {formatCurrency(selectedLoan.Plafond)} <br />
                    <strong>Sisa Pokok:</strong> {formatCurrency(selectedLoan.SisaPokok)} <br />
                    <strong>Bunga:</strong> {selectedLoan.Bunga}% per tahun <br />
                    <strong>Tgl Pencairan:</strong> {selectedLoan.TglPencairan ? new Date(selectedLoan.TglPencairan).toLocaleDateString('id-ID') : '-'}
                  </div>

                  {calculation && (
                    <div className="alert alert-secondary mb-3">
                      <h6 className="alert-heading">Perhitungan Pembayaran</h6>
                      <table className="table table-sm mb-0">
                        <tbody>
                          <tr>
                            <td>Jumlah bulan yang belum dibayar</td>
                            <td className="text-right">{calculation.monthsCount} bulan</td>
                          </tr>
                          <tr>
                            <td>Bunga per bulan</td>
                            <td className="text-right">{formatCurrency(calculation.bungaPerBulan)}</td>
                          </tr>
                          <tr className="table-info">
                            <td>
                              {calculation.isProrataApplied 
                                ? `Total bunga prorata (${calculation.daysSinceLastPayment} hari)` 
                                : `Total bunga (${calculation.monthsCount} bulan)`}
                            </td>
                            <td className="text-right">{formatCurrency(calculation.bungaBulanBerjalan)}</td>
                          </tr>
                          {calculation.isProrataApplied && (
                            <tr className="table-light">
                              <td><small className="text-muted">&nbsp;&nbsp;Bunga per bulan / 30 × {calculation.daysSinceLastPayment} hari</small></td>
                              <td className="text-right"><small className="text-muted">{formatCurrency(calculation.bungaPerBulan)} / 30 × {calculation.daysSinceLastPayment}</small></td>
                            </tr>
                          )}
                          <tr>
                            <td>Denda keterlambatan</td>
                            <td className="text-right">{formatCurrency(calculation.denda)}</td>
                          </tr>
                          <tr className="table-primary">
                            <td><strong>Total Kewajiban</strong></td>
                            <td className="text-right"><strong>{formatCurrency(calculation.totalKewajiban)}</strong></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">Jumlah Bayar *</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.jumlahBayar}
                      onChange={(e) => handleJumlahBayarChange(e.target.value)}
                      required
                      min="0"
                      step="1000"
                    />
                  </div>

                  {allocation && (
                    <div className={`alert ${allocation.isComplete ? 'alert-success' : 'alert-warning'} mb-3`}>
                      <h6 className="alert-heading">Alokasi Pembayaran</h6>
                      <table className="table table-sm mb-0">
                        <tbody>
                          <tr>
                            <td>Jumlah Bayar</td>
                            <td className="text-right">{formatCurrency(allocation.jumlahBayar)}</td>
                          </tr>
                          <tr className="table-warning">
                            <td>Alokasi Bunga ({calculation.monthsCount} bulan)</td>
                            <td className="text-right">- {formatCurrency(allocation.totalAlokasiBunga || allocation.alokasiBunga)}</td>
                          </tr>
                          {(allocation.alokasiTunggakanBunga > 0 || allocation.tunggakanBunga > 0) && (
                            <tr className="table-info">
                              <td>&nbsp;&nbsp;- Tunggakan Bunga</td>
                              <td className="text-right">- {formatCurrency(allocation.alokasiTunggakanBunga || 0)}</td>
                            </tr>
                          )}
                          {(allocation.alokasiDenda > 0 || allocation.alokasiTunggakanDenda > 0 || allocation.totalAlokasiDenda > 0) && (
                            <tr className="table-warning">
                              <td>Alokasi Denda</td>
                              <td className="text-right">- {formatCurrency(allocation.totalAlokasiDenda || (allocation.alokasiDenda + allocation.alokasiTunggakanDenda))}</td>
                            </tr>
                          )}
                          {(allocation.alokasiTunggakanDenda > 0 || allocation.tunggakanDenda > 0) && (
                            <tr className="table-info">
                              <td>&nbsp;&nbsp;- Tunggakan Denda</td>
                              <td className="text-right">- {formatCurrency(allocation.alokasiTunggakanDenda || 0)}</td>
                            </tr>
                          )}
                          <tr className="table-success">
                            <td>Alokasi Pokok Pinjaman</td>
                            <td className="text-right">- {formatCurrency(allocation.alokasiPokok)}</td>
                          </tr>
                          <tr className="table-primary">
                            <td><strong>Sisa Pokok Setelah Bayar</strong></td>
                            <td className="text-right"><strong>{formatCurrency(allocation.sisaPokokBaru)}</strong></td>
                          </tr>
                        </tbody>
                      </table>
                      {!allocation.isComplete && allocation.kewajibanBungaDenda > 0 && (
                        <div className="mt-2 text-danger">
                          <small><i className="fas fa-exclamation-circle"></i> Jumlah bayar kurang dari kewajiban bunga+denda ({formatCurrency(allocation.kewajibanBungaDenda)})</small>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">Sumber Dana *</label>
                    <select
                      className="form-control"
                      value={formData.sumberDana}
                      onChange={(e) => setFormData({ ...formData, sumberDana: e.target.value })}
                      required
                    >
                      <option value="kas">Kas (Tunai)</option>
                      <option value="bank">Bank Transfer</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Keterangan</label>
                    <textarea
                      className="form-control"
                      value={formData.keterangan}
                      onChange={(e) => setFormData({ ...formData, keterangan: e.target.value })}
                      rows="2"
                    />
                  </div>

                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Memproses...' : 'Bayar'}
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      </div>

      {showPrintModal && printData && (
        <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-success">
                <h5 className="modal-title">Bukti Pembayaran Pinjaman</h5>
                <button type="button" className="close text-white" onClick={() => setShowPrintModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPrintModal(false)}>
                  Tutup
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    const receiptContent = `
================================
  BUKTI PEMBAYARAN PINJAMAN
================================
No Pinjaman: ${printData.noPinjaman}
Tanggal    : ${printData.tanggal}
---------------------------------
DATA ANGGOTA
---------------------------------
NBA        : ${printData.nba}
Nama       : ${printData.namaAnggota}
---------------------------------
DETAIL PEMBAYARAN
---------------------------------
Jumlah Bayar    : Rp ${parseFloat(printData.jumlahBayar).toLocaleString('id-ID')}
---------------------------------
ALOKASI PEMBAYARAN
---------------------------------
Bunga           : Rp ${parseFloat(printData.alokasiBunga).toLocaleString('id-ID')}
Pokok           : Rp ${parseFloat(printData.alokasiPokok).toLocaleString('id-ID')}
Denda           : Rp ${parseFloat(printData.alokasiDenda).toLocaleString('id-ID')}
---------------------------------
SISA SETELAH BAYAR
---------------------------------
Sisa Pokok      : Rp ${parseFloat(printData.sisaPokokBaru).toLocaleString('id-ID')}
Tunggakan Bunga : Rp ${parseFloat(printData.tunggakanBungaBaru).toLocaleString('id-ID')}
Tunggakan Denda : Rp ${parseFloat(printData.tunggakanDendaBaru).toLocaleString('id-ID')}
================================
  Tanda Tangan

(${namaKoperasi})
================================
`
                    const printWindow = window.open('', '_blank')
                    printWindow.document.write(`
                      <html>
                        <head>
                          <title>Bukti Pembayaran Pinjaman</title>
                          <style>
                            body {
                              font-family: 'Courier New', monospace;
                              font-size: 12px;
                              margin: 0;
                              padding: 10px;
                              white-space: pre;
                              line-height: 1.2;
                            }
                          </style>
                        </head>
                        <body>${receiptContent}</body>
                      </html>
                    `)
                    printWindow.document.close()
                    printWindow.print()
                  }}
                >
                  <i className="fas fa-print mr-1"></i> Cetak
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LoanPayment
